import ApprovalStatus from '@/constants/ApprovalStatus';
import OnlinePaymentStatus from '@/constants/OnlinePaymentStatus';
import { computed, ref } from 'vue';

export const openGroups = ref([]);

export const acceptedFile = (t, file, type = 'image') => {
  if (!file || !file.size) {
    return t('fileIsEmpty');
  }
  let maxSize = window.app_configs?.max_upload_size || 1024 * 1024;
  if (file.size > maxSize) {
    return t('fileSizeToBig', { size: (file.size / 1024).toFixed(2), maxSize: parseInt(maxSize / 1024) });
  }
  const supportedFiles = getSupportedFiles(type).split(',');
  return supportedFiles.includes(file.type) ? null : t('fileFormatNotAccepted');
};

export const getSupportedFiles = (type = 'image') => {
  const docs = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
  ];
  const images = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/webp'];
  const videos = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/mkv'];
  const audios = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp3'];

  switch (type) {
    case 'image':
      return import.meta.env.VITE_APP_SUPPORTED_FILES_IMAGE || images.join(',');
    case 'video':
      return import.meta.env.VITE_APP_SUPPORTED_FILES_VIDEO || videos.join(',');
    case 'audio':
      return import.meta.env.VITE_APP_SUPPORTED_FILES_AUDIO || audios.join(',');
    case 'document':
      return import.meta.env.VITE_APP_SUPPORTED_FILES_DOCUMENT || docs.join(',');
    default:
      return import.meta.env.VITE_APP_SUPPORTED_FILES_ALL || [...images, ...docs, ...videos, ...audios].join(',');
  }
};

export const openLink = (url, target = '_blank') => {
  const link = document.createElement('a');
  link.href = url;
  link.target = target;
  link.rel = 'noreferrer noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getApprovalColor = status => {
  const approvalStatus = Object.values(ApprovalStatus).find(s => s.value === status);
  return approvalStatus ? approvalStatus.color : ApprovalStatus.PENDING.color;
};

export const getApprovalName = status => {
  const approvalStatus = Object.values(ApprovalStatus).find(s => s.value === status);
  return approvalStatus ? approvalStatus.text : ApprovalStatus.PENDING.text;
};

export const getPaymentColor = status => {
  const paymentStatus = Object.values(OnlinePaymentStatus).find(s => s.value === status);
  return paymentStatus ? paymentStatus.color : OnlinePaymentStatus.INITIALIZED.color;
};

export const getPaymentName = status => {
  const paymentStatus = Object.values(OnlinePaymentStatus).find(s => s.value === status);
  return paymentStatus ? paymentStatus.text : OnlinePaymentStatus.INITIALIZED.text;
};

export const getConfigByKey = (key = '', sub_key = '') => {
  if (window.app_configs && (!key || window.app_configs[key])) {
    let val = key ? window.app_configs[key] : window.app_configs;
    if (sub_key && key == 'config') {
      for (let k in val) {
        if (sub_key == val[k]?.key) {
          return Array.isArray(val[k]?.data) ? [...val[k]?.data] : { ...val[k]?.data };
        }
      }
      return {};
    }

    return [...val];
  }

  return [];
};

/**
 * Return nav link props to use
 * @param {Object, String} item navigation routeName or route Object provided in navigation data
 */
export const getComputedNavLinkToProp = computed(() => link => {
  const props = {
    target: link.target,
    rel: link.rel,
  };

  // If route is string => it assumes string is route name => Create route object from route name
  // If route is not string => It assumes it's route object => returns passed route object
  if (link.to) props.to = typeof link.to === 'string' ? { name: link.to } : link.to;
  else props.href = link.href;

  return props;
});

/**
 * Return route name for navigation link
 * If link is string then it will assume it is route-name
 * IF link is object it will resolve the object and will return the link
 * @param {Object, String} link navigation link object/string
 */
export const resolveNavLinkRouteName = (link, router) => {
  if (!link.to) return null;
  if (typeof link.to === 'string') return link.to;

  return router.resolve(link.to).name;
};

/**
 * Check if nav-link is active
 * @param {Object} link nav-link object
 */
export const isNavLinkActive = (link, router) => {
  // Matched routes array of current route
  const matchedRoutes = router.currentRoute.value.matched;

  // Check if provided route matches route's matched route
  const resolveRoutedName = resolveNavLinkRouteName(link, router);
  if (!resolveRoutedName) return false;

  return matchedRoutes.some(route => {
    return route.name === resolveRoutedName || route.meta.navActiveLink === resolveRoutedName;
  });
};

/**
 * Check if nav group is active
 * @param {Array} children Group children
 */
export const isNavGroupActive = (children, router) =>
  children.some(child => {
    // If child have children => It's group => Go deeper(recursive)
    if ('children' in child) return isNavGroupActive(child.children, router);

    // else it's link => Check for matched Route
    return isNavLinkActive(child, router);
  });

/**
 * Convert Hex color to rgb
 * @param hex
 */
export const hexToRgb = hex => {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;

  hex = hex.replace(shorthandRegex, (m, r, g, b) => {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result ? `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}` : null;
};

// 👉 IsEmpty
export const isEmpty = value => {
  if (value === null || value === undefined || value === '') return true;

  return !!(Array.isArray(value) && value.length === 0);
};

// 👉 IsNullOrUndefined
export const isNullOrUndefined = value => {
  return value === null || value === undefined;
};

// 👉 IsEmptyArray
export const isEmptyArray = arr => {
  return Array.isArray(arr) && arr.length === 0;
};

// 👉 IsObject
export const isObject = obj => obj !== null && !!obj && typeof obj === 'object' && !Array.isArray(obj);
export const isToday = date => {
  const today = new Date();

  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
};
